import { isObject } from 'util';

import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, IeAnexo, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { FetchAPI, FieldAPI, FileUploaderAPI, FormAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ButtonTheme, ModalButtonType } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnCancel, OnClose, OnEdit, OnMount, OnSelect, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { AbortableSubmitEvent, MountEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HistoryService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';
import { isContentfulString, isEmptyObject } from '@philips-emr/tws-utils';

import { ProductChangeAliases as Aliases, FragmentNames } from '../codegen';
import { Anexo } from '../interfaces/anexo';

import BaseController from './BaseController';

interface SolicitacaoAlteracaoProduto {
  dsPlanoAtual: string;
  nrSeqPlano: number;
  nrSeqPlanoNovo: number;
  nrSeqSolicAlt: number;
}

interface Retorno {
  response: Record<string, string>;
  nrSeqAnexo: number;
  filepath: string;
  filename: string;
}

interface Mensagem {
  DS_RETORNO: string;
}

@Controller({ fragment: FragmentNames.PRODUCT_CHANGE, component: Aliases.LAYOUT_PRODUCT_CHANGE })
class ProductChangeController extends BaseController {

  private regraAnexos: Anexo[];

  @Inject(Aliases.PRODUTOS_FETCH)
  private fetchLookUp: FetchAPI;
  @Inject(Aliases.FILE_UPLOADER)
  private fileUploaderAPI: FileUploaderAPI;
  @Inject(Aliases.FORM_PRODUCT_CHANGE)
  private productForm: FormAPI;
  @Inject(Aliases.FETCH_LISTA_ANEXOS)
  private fetchListaAnexos: FetchAPI;
  @Inject(Aliases.FIELD_PRODUTO)
  private fieldProduto: FieldAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastForm: ToastNotificationAPI;
  @Inject(Aliases.FIELD_FILE_UPLOADER)
  private fieldUpload: FieldAPI;
  @Inject(Aliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;

  private retorno: boolean;

  @OnMount(Aliases.PRODUTOS_FETCH)
  onMount(): void {
    const { nrSeqContrato, nrSeqPlanoAtual } = this.context.getBulkData();
    this.fetchLookUp.setParameters({ nrSeqContrato, nrSeqPlanoAtual });
  }

  @OnMount(Aliases.AUTOCOMPLETE_PRODUTOS)
  onMountProdutos(): void {
    this.getPredefinedFiles();
  }

  @OnMount(Aliases.FETCH_LISTA_ANEXOS)
  onMountPanelList(): void {
    this.getListaAnexos();
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Record<string, string>>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value as unknown as Retorno;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.SEG_SOLIC_ALT_ANEXO;

    FileService.download(Endpoint.GET.ALTERACAO_PRODUTO_ANEXO_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnMount(Aliases.FORM_PRODUCT_CHANGE)
  onFormMount(event: MountEvent): void {
    const apiForm = event.target as unknown as FormAPI;
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();

    void HttpService.get<Record<string, Value>>(Endpoint.GET.SOLIC_ALT_ABERTA, { nrSeqBeneficiario, nrSeqContrato }).then(response => {

      const { body: solicAberta } = response;

      this.getPredefinedFiles();

      void HttpService.get<Record<string, Value>>(Endpoint.GET.PRODUTO_BENEFICIARIO, { nrSeqBeneficiario, nrSeqContrato }).then(resp => {
        const { body: solicAlteracao } = resp;
        const data = { ...solicAlteracao, ...solicAberta };
        const { nrSeqPlano, nrSeqPlanoNovo, nrSeqSolicAlt } = data as unknown as SolicitacaoAlteracaoProduto;

        const nrSeqPlanoAtual = nrSeqPlanoNovo ? nrSeqPlanoNovo : nrSeqPlano;

        this.context.set({ nrSeqPlanoAtual, nrSeqSolicAlt });

        if (solicAberta) {
          this.retorno = true;
          this.context.set({ solicAberta });
          apiForm.setData(data);
          this.fieldProduto.setVisible(true);
          this.getListaAnexos();
        } else {
          apiForm.setEditing(true);
          this.fieldProduto.setVisible(false);
        }
      });
    });
  }

  @OnEdit(Aliases.FORM_PRODUCT_CHANGE)
  onEdit(): void {
    this.getPredefinedFiles();
    this.retorno = true;
    this.panelLoader.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_PRODUCT_CHANGE)
  onSubmitEvent(event: AbortableSubmitEvent): void {
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.panelLoader.setVisible(true);
    const dados = this.productForm.getRecord();

    dados.anexos = this.handleAnexos(dados.anexos as Anexo[]);

    const body = { nrSeqBeneficiario, ...event.record };
    HttpService.post(Endpoint.POST.SOLIC_ALT_PRODUTO, { nrSeqContrato }, { body })
      .then(responseGet => {
        this.panelLoader.setVisible(false);
        const { body } = responseGet;
        const { response } = body as Retorno;
        const { DS_RETORNO } = response as unknown as Mensagem;
        this.getListaAnexos();
        ModalService.openDialog(this.newModalDefaultContent(DS_RETORNO));
        return false;
      })
      .catch(response => {
        this.panelLoader.setVisible(false);
        this.productForm.setEditing(true);
        this.toastForm.setVisible(true);
        const { body } = response;
        const openToast = (msg: string): void => {
          this.toastForm.setMessage(msg);
          this.productForm.setDisabled(false);
        };

        if (Array.isArray(body)) {
          this.productForm.setEditing(true);
          const data = body as Anexo[];
          const message = `${MessageService.translate('stipulator.obrigatorio-anexar-arquivo')} ${data.map(anexo => anexo.dsLabelCampo).join(', ')}.`;
          return openToast(message);
        }

        if (isObject(body) && !isEmptyObject(body)) {
          return openToast(body[Object.keys(body)[0]]);
        }

        if (isContentfulString(body)) {
          return openToast(body);
        }
      });
  }

  @OnCancel(Aliases.FORM_PRODUCT_CHANGE)
  oncancel(): void {

    if (!this.retorno) {
      const { nrSeqGrupoContratoWeb, nrSeqContrato } = HistoryService.getCurrentRouteParams();
      HistoryService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO, { nrSeqGrupoContratoWeb, nrSeqContrato });
      return;
    }
    this.getListaAnexos();
    this.retorno = false;
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.toastForm.setVisible(false);
  }

  private getPredefinedFiles(): void {
    void HttpService.get(Endpoint.GET.CHECK_TIPO_ANEXOS, { ieFuncionalidade: '13' })
      .then(response => {
        this.fieldUpload.setVisible(true);
        const anexos = response.body as Anexo[];
        const predefinedFiles = anexos.map(regras => {
          const { dsLabelCampo: value, nrSeqRegraAnexo: key } = regras;
          return { key: String(key), value };
        });
        this.regraAnexos = anexos;
        catchRenderException(() => this.fileUploaderAPI.setPredefinedFiles(predefinedFiles));
        if (predefinedFiles.length == 0) {
          this.fieldUpload.setVisible(false);
        }
      });
  }

  private getListaAnexos(): void {
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Record<string, Value>>(Endpoint.GET.SOLIC_ALT_ABERTA, { nrSeqBeneficiario, nrSeqContrato }).then(response => {
      const { body: solicAberta } = response;
      if (solicAberta) {
        const { nrSeqSolicAlt } = solicAberta;
        this.fetchListaAnexos.setParameters({ nrSeqBeneficiario, nrSeqSolicAlt });
        this.fetchListaAnexos.forceFetch();
        this.fieldAnexos.setVisible(true);
        return;
      }
    });
  }

  private newModalDefaultContent(dsMensagem: string): ModalProps {
    const modalDefaultContent: ModalProps = {
      closeable: false,
      fragment: 'mensagem-modal',
      contextData: { dsMensagem },
      title: MessageService.translate('stipulator.aviso-solicitacao-realizada'),
      dockButtons: [{
        action: () => {this.historyService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO);},
        enabled: true,
        theme: ButtonTheme.PRIMARY,
        title: MessageService.translate('$expression.$311702'),
        type: ModalButtonType.SUCCESS,
      }],
    };

    return modalDefaultContent;
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}
export default ProductChangeController;
